import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { constructUrl, getParams } from "@/app/_module/services/helper";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";

import type { GetPurchaseHistoryParams, PurchaseHistoryResponse, PurchaseStats } from "../types/transactions";

export const transactionService = {
  getPurchaseHistory: async (param: GetPurchaseHistoryParams) => {
    let params;
    if (param) {
      params = getParams<GetPurchaseHistoryParams>(param);
    }

    const url = params
      ? constructUrl(getApiUrl(endpoint.transactions.getPurchaseHistory), params)
      : getApiUrl(endpoint.transactions.getPurchaseHistory);

    try {
      // handleApiCall now automatically extracts the data from the backend's standardized response
      // So it returns the actual data directly, not wrapped in { data: {...} }
      const data = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: PurchaseHistoryResponse;
        timestamp: string;
      }>({
        url,
        auth: true,
      }));

      // handleApiCall already extracted the data, so return it directly
      return data;
    } catch (error: unknown) {

      // TEMPORARY: Throw error to surface it instead of hiding it
      throw error;

      // OLD CODE (silently returning empty data - DO NOT USE):
      // return {
      //   meta: {
      //     currentPage: 1,
      //     totalPages: 0,
      //     totalItems: 0,
      //     itemsPerPage: 10,
      //     hasNextPage: false,
      //     hasPreviousPage: false
      //   },
      //   items: []
      // };
    }
  },

  getPurchaseHistoryStats: async () => {
    try {
      // handleApiCall already extracts the data from the standardized response
      const data = await handleApiCall(async () => await api.get<{
        statusCode: number;
        message: string;
        data: PurchaseStats;
        timestamp: string;
      }>({
        url: getApiUrl(endpoint.transactions.getPurchaseHistoryStats),
        auth: true,
      }));

      // handleApiCall already extracted the data, so use it directly
      return {
        totalSpent: Number((data as any)?.totalSpent) || 0,
        totalOrders: Number((data as any)?.totalOrders) || 0,
        currency: (data as any)?.currency || 'USD',
        averageOrderValue: Number((data as any)?.averageOrderValue) || 0,
        totalRefunded: Number((data as any)?.totalRefunded) || 0,
        currentPlan: (data as any)?.currentPlan,
        nextBillingDate: (data as any)?.nextBillingDate,
        autoRenew: (data as any)?.autoRenew,
        lastPurchaseDate: (data as any)?.lastPurchaseDate,
      };
    } catch (error: unknown) {
      // TEMPORARY: Throw error to surface it instead of hiding it
      throw error;

      // OLD CODE (silently returning default stats - DO NOT USE):
      // return {
      //   totalSpent: 0,
      //   totalOrders: 0,
      //   currency: 'USD',
      //   averageOrderValue: 0,
      //   totalRefunded: 0,
      //   currentPlan: null,
      //   nextBillingDate: null,
      //   lastPurchaseDate: null,
      // };
    }
  },

  getTransactionById: async (id: string) => {
    try {
      const response = await handleApiCall(async () => await api.get<unknown>({
        url: getApiUrl(endpoint.transactions.getTransactionById(id)),
        auth: true, // Add authentication
      }));

      return response?.data;
    } catch (error: unknown) {
      // TEMPORARY: Throw error to surface it instead of hiding it
      throw error;

      // OLD CODE (silently returning null - DO NOT USE):
      // return null;
    }
  },

  cancelTransaction: async (transactionId: string) => {
    try {
      const response = await handleApiCall(async () => await api.post<{
        statusCode: number;
        message: string;
        data: { cancelled: boolean; transactionId: string };
        timestamp: string;
      }>({
        url: getApiUrl(endpoint.transactions.cancelTransaction),
        body: { transactionId },
        auth: true,
      }));

      showToast("Transaction cancelled successfully", "success");
      return response;
    } catch (error: unknown) {
      const errorMessage = transformErrorResponse(error)?.message || "Failed to cancel transaction";
      showToast(errorMessage, "error");
      throw error;
    }
  },
};
